import {
  askClaude,
  askGemini,
  askGroq,
  askOpenAI,
  askVolcengine,
  type ProviderResult,
} from '../../workers/lib/providers';
import { API_BASE } from '../api-base';
import type { AIProviderName } from '../types';
import { STORAGE_KEYS } from '../ui/settings';
import { isOverLimit, recordUsage } from '../usage';
import { PROVIDER_WIN_KEYS } from './keys';

const KNOWN_PROVIDERS = ['gemini', 'groq', 'openai', 'claude', 'volcengine'] as const;

function isKnownProvider(p: string): p is AIProviderName {
  return (KNOWN_PROVIDERS as readonly string[]).includes(p);
}

// File-local helper — collapses two identical signal-check throw sites in askAI.
function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
}

type Win = Record<string, string | undefined>;

function getWindowKey(globalName: string): string | null {
  const v = (window as unknown as Win)[globalName];
  return v?.trim() || null;
}

interface KeyEntry {
  name: AIProviderName;
  winKey: string;
  fn: (key: string, prompt: string) => Promise<ProviderResult>;
}

// Mark which provider answered last (lights up the active row in settings)
// and fire the state-change event so any open settings panel re-renders.
function markActiveProvider(p: AIProviderName): void {
  localStorage.setItem(STORAGE_KEYS.lastProvider, p);
  window.dispatchEvent(new CustomEvent('linyuan:ai-state-change'));
}

// BYOK success: bump the daily counter (server-side quota lives elsewhere —
// the counter only tracks BYOK to drive the daily-limit gate) AND mark
// the provider active. Backend-proxy success uses markActiveProvider only,
// so server-handled requests don't consume the user's BYOK budget.
function recordByokSuccess(p: AIProviderName): void {
  recordUsage(p);
  markActiveProvider(p);
}

const VOLCENGINE_ENTRY: KeyEntry = {
  name: 'volcengine',
  winKey: PROVIDER_WIN_KEYS.volcengine,
  fn: askVolcengine,
};
const NON_CN_CHAIN: KeyEntry[] = [
  { name: 'gemini', winKey: PROVIDER_WIN_KEYS.gemini, fn: askGemini },
  { name: 'groq', winKey: PROVIDER_WIN_KEYS.groq, fn: askGroq },
  { name: 'openai', winKey: PROVIDER_WIN_KEYS.openai, fn: askOpenAI },
  { name: 'claude', winKey: PROVIDER_WIN_KEYS.claude, fn: askClaude },
];

// BYOK hits providers directly, so GFW matters here. Any zh-* user is a
// reasonable bet to be reading from a network where Gemini/Groq/OpenAI/
// Claude can't be reached, so put Volcengine first across all zh variants
// (zh, zh-CN, zh-Hans, zh-Hant, zh-TW, zh-HK). The cost of misordering
// for a non-mainland zh reader is one extra Volcengine attempt that
// succeeds; the cost of NOT reordering for a mainland zh-Hant reader is
// four 30-second timeouts before the chain reaches Volcengine.
function byokChain(): KeyEntry[] {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '').toLowerCase();
  const isZh = lang.startsWith('zh');
  return isZh ? [VOLCENGINE_ENTRY, ...NON_CN_CHAIN] : [...NON_CN_CHAIN, VOLCENGINE_ENTRY];
}

/**
 * Send `prompt` to the AI layer. Tries the backend proxy at `/api/ai/read` first
 * (server holds keys); falls back to BYOK keys stored in window globals if the
 * backend is unreachable, returns no text, or returns non-2xx.
 *
 * Keys are in window globals (GEMINI_API_KEY, etc.) — settings.ts writes them
 * there both for plain-mode (from localStorage) and encrypted-mode (after decrypt).
 *
 * Always records usage against the provider that succeeded so the daily counters
 * in `src/usage.ts` stay accurate for the BYOK status line.
 */
export async function askAI(prompt: string, signal?: AbortSignal): Promise<ProviderResult> {
  // Step 1: backend proxy
  try {
    const r = await fetch(`${API_BASE}/api/ai/read`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ prompt }),
      signal,
    });
    if (r.ok) {
      const body = (await r.json()) as Partial<ProviderResult> & { error?: string };
      if (body.text?.trim() && body.provider) {
        if (isKnownProvider(body.provider)) markActiveProvider(body.provider);
        return { text: body.text, provider: body.provider, model: body.model };
      }
    }
    // 2xx-but-empty or non-2xx — fall through to BYOK
  } catch (e) {
    // If the caller aborted, don't fall through to non-abortable BYOK.
    if ((e as Error)?.name === 'AbortError') throw e;
    // Other network errors — fall through to BYOK
  }
  throwIfAborted(signal);

  // Step 2: BYOK fallback — try each provider whose key is in window globals.
  // Check signal between providers so a back-arrow / dispose during BYOK
  // stops the chain instead of running every provider serially.
  // BYOK provider functions don't accept a signal today, so an in-flight
  // provider call cannot be aborted mid-request — only between providers.
  // The signal check at the top of the loop is what makes the chain stop.
  let lastErr: Error | null = null;
  for (const { name, winKey, fn } of byokChain()) {
    throwIfAborted(signal);
    const key = getWindowKey(winKey);
    if (!key) continue;
    if (isOverLimit(name)) continue;
    try {
      const res = await fn(key, prompt);
      if (res.text.trim()) {
        recordByokSuccess(name);
        return res;
      }
    } catch (e) {
      // Re-throw aborts so a back-arrow / dispose during BYOK actually
      // stops the chain instead of silently continuing through the
      // remaining providers. Matches the backend-proxy try/catch above.
      if ((e as Error)?.name === 'AbortError') throw e;
      lastErr = e as Error;
      console.warn(`byok ${name} failed:`, lastErr.message);
    }
  }

  const finalErr = lastErr ?? new Error('ai-unavailable');
  // Surface for any listener (the dev settings panel mounts one). Cheap no-op
  // in prod since the panel — and its listener — only attach when DEV.
  window.dispatchEvent(
    new CustomEvent('linyuan:ai-error', {
      detail: { message: finalErr.message, when: Date.now() },
    }),
  );
  throw finalErr;
}
